<template>
  <nav class="flex items-center h-10 gap-2 xs:gap-4">
    <BrowserBackButton v-show="flags.iframe_mode" class="mr-auto h-full" />
    <BrandLogo v-show="!flags.iframe_mode" class="mr-auto" />

    <Skeleton
      v-if="loadingUser && logo && flags.enable_notifications"
      class="h-8 w-8"
    />
    <NotificationMenu
      v-if="!loadingUser && logo && flags.enable_notifications"
      :logo="logo"
    />

    <Skeleton
      v-if="loadingUser && flags.enable_multi_currency_and_language"
      class="h-9 w-10 rounded-lg"
      :class="{ 'xs:w-20': !isCurrencySet }"
    />
    <CurrencyAndLanguageSelection
      v-if="!loadingUser && flags.enable_multi_currency_and_language"
      v-bind="{ isCurrencySet }"
    />

    <div>
      <!-- Loader  -->
      <div
        v-show="loadingUser"
        class="ml-2 relative z-1 flex-shrink-0 outline-none flex items-center"
      >
        <Skeleton v-if="loadingUser" class="h-8 w-8 rounded-full" />
      </div>
      <!-- Not signed in -->
      <div
        v-show="!loadingUser"
        class="relative flex-shrink-0 outline-none"
        tabindex="0"
        @blur="menuOpen = false"
      >
        <div v-show="!loggedIn">
          <n-link
            class="flex items-center"
            :to="{ path: `/${link}`, query: { ...$route.query } }"
          >
            <Avatar
              class="h-8 w-8"
              :pad-icon="false"
              :icon="UserIcon"
              color="transparent"
            />
          </n-link>
        </div>

        <!-- Signed in -->
        <div v-show="loggedIn">
          <div
            id="user-menu"
            class="flex group items-center cursor-pointer"
            @click="menuOpen = !menuOpen"
          >
            <p
              v-show="name"
              class="text-right mr-3 font-medium hidden sm:flex flex-col"
            >
              <span
                class="name text-sm text-gray-600 group-hover:text-enterprise-primary"
              >
                {{ name }}
              </span>
              <span
                class="text-xs text-gray-500 group-hover:text-enterprise-primary"
              >
                Your Account
              </span>
            </p>
            <div class="relative flex items-center">
              <Avatar
                class="h-8 w-8"
                :pad-icon="false"
                :src="avatar"
                :icon="UserIcon"
                color="transparent"
              />
              <Crown
                v-if="hasBrandPlan"
                class="absolute top-0 right-0 transform translate-x-1.5 -translate-y-1.5"
              />
            </div>
          </div>
          <client-only>
            <transition name="dropdown">
              <div
                v-show="menuOpen"
                class="origin-top-right absolute top-full right-0 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-1 z-20 "
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="user-menu"
                @click="menuOpen = false"
              >
                <div class="text-left text-sm break-words py-2 px-4 border-b">
                  <p v-show="name">
                    {{ name }}
                  </p>
                  <p v-if="user" class="text-xs text-gray-500">
                    {{ user.email }}
                  </p>
                </div>
                <span
                  v-for="{ to, label } in links"
                  :key="to"
                  class="block cursor-pointer text-left w-full py-2 px-4 text-sm text-gray-700 force-color hover:bg-gray-100"
                  role="menuitem"
                  @click="$router.push(to)"
                >
                  {{ label }}
                </span>
                <span
                  v-show="featureIsEnabled('account')"
                  class="block cursor-pointer text-left w-full py-2 px-4 text-sm text-gray-700 force-color hover:bg-gray-100"
                  role="menuitem"
                  @click="logout"
                >
                  Sign out
                </span>
              </div>
            </transition>
          </client-only>
        </div>
      </div>
    </div>

    <slot />
  </nav>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import UserIcon from '@/assets/svg/icons/outline/user-circle.svg?raw'
import BrowserBackButton from '@/components/BrowserBackButton'
import Crown from '@/components/Account/brand-plan/Crown'
import BrandLogo from '@/components/BrandLogo'
import Avatar from '@/components/Avatar'
import Skeleton from '@/components/loader/Skeleton'
import CurrencyAndLanguageSelection from '@/components/CurrencyAndLanguageSelection'
import NotificationMenu from '@/components/notifications/NotificationMenu'
import user from '@/mixins/user'
import auth from '@/mixins/auth'
import { useLogos } from '@/lib/hooks'
import { EventBus, Events } from '@/lib/events'

export default {
  components: {
    BrowserBackButton,
    BrandLogo,
    Avatar,
    Crown,
    Skeleton,
    CurrencyAndLanguageSelection,
    NotificationMenu,
  },

  mixins: [auth, user],

  props: {
    logo: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    menuOpen: false,
    logos: [],
    UserIcon,
  }),

  mounted() {
    EventBus.$on(Events.UserUpdatedAttributes, () => this.getUser())
  },

  computed: {
    ...mapState(['flags']),

    links() {
      const dashboardLink = `/dashboard/${this.logo?.id || 'latest'}`

      const links = [
        { to: dashboardLink, label: 'Dashboard' },
        { to: '/dashboard', label: 'Your Logos' },
      ]

      // if (this.featureIsEnabled('account')) {
      links.push({
        to: `${this.logos.length ? dashboardLink : ''}/account`,
        label: 'Account',
      })
      // }

      return links
    },

    /**
     * Returns the correct header page based on the Auth state and the current Route
     */
    link() {
      if (this.loggedIn) {
        return 'dashboard'
      }

      return this.$route.name === 'login' ? 'register' : 'login'
    },

    isCurrencySet() {
      if (!this.user) return false

      return this.user.is_currency_set
    },

    ...mapGetters({ featureIsEnabled: 'app/featureIsEnabled' }),
  },

  async created() {
    this.fetchLogos()
  },

  methods: {
    /**
     * Fetches the logos for the user.
     */
    async fetchLogos() {
      const { listAllSavedLogos } = useLogos(this.$api)

      try {
        const { data } = await listAllSavedLogos()
        this.logos = data
      } catch (e) {
        return
      }
    },
  },

  watch: {
    $route() {
      this.menuOpen = false
    },
  },
}
</script>

<style scoped>
.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 4rem;
}

@media (min-width: 24.375em) {
  .name {
    max-width: 6rem;
  }
}
@media (min-width: 28em) {
  .name {
    max-width: 8rem;
  }
}
@media (min-width: 40em) {
  .name {
    max-width: 12rem;
  }
}
</style>
